import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';

import { MomentModule } from 'ngx-moment';

import type { Attachment } from '@recruitee/attachments-types';
import { TranslatePipe } from '@recruitee/i18n';
import {
  AttachmentItemComponent,
  IconComponent,
  RtTooltip,
  ZiggeoPlayerComponent,
} from '@recruitee/user-interface';
import { LinkWrapperComponent } from '@recruitee/utils';
import type { Visibility } from '@recruitee/visibility-types';
import { VisibilityLevel } from '@recruitee/visibility-types';

import type { CandidateQuestionsItem } from './candidate-questions-item.interfaces';
import { QuestionKind } from '../../candidate-questions.interfaces';
import type { CandidateQuestionsModuleTranslations } from '../../translations';
import { CandidateQuestionsTranslations } from '../../translations';

@Component({
  selector: 'rt-candidate-questions-item',
  templateUrl: './candidate-questions-item.component.html',
  styleUrls: ['./candidate-questions-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-private]': 'visibility && visibility.level !== VisibilityLevel.Public',
  },
  imports: [
    IconComponent,
    RtTooltip,
    NgTemplateOutlet,
    AttachmentItemComponent,
    ZiggeoPlayerComponent,
    TranslatePipe,
    MomentModule,
    LinkWrapperComponent,
  ],
})
export class CandidateQuestionsItemComponent {
  public readonly QuestionKind: typeof QuestionKind = QuestionKind;
  public readonly VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;

  @Input() public visibilityTplRef: TemplateRef<any>;
  @Input() public data: CandidateQuestionsItem;
  @Input() public canManageCandidates: boolean = false;
  @Input() public hasBooleanFlagAnswers: boolean = true;

  public get title(): string {
    return this.data.question;
  }

  public get content(): string {
    return this.data.content!;
  }

  public get contents(): string[] {
    return this.data.contents!;
  }

  public get hasBooleanAnswer(): boolean {
    return typeof this.data.flag === 'boolean';
  }

  public get booleanAnswer(): boolean {
    return this.data.flag!;
  }

  public get kind(): string {
    return this.data.kind;
  }

  public get visibility(): Visibility {
    return this.data.visibility!;
  }

  public get visible(): boolean {
    return typeof this.data.visible === 'boolean' ? this.data.visible : true;
  }

  public get attachments(): Attachment[] {
    const attachments = this.data.attachment ? [this.data.attachment] : this.data.attachments;
    return attachments || [];
  }

  public get ratingScale(): number {
    return this.data.questionOptions!.scale === '1-5' ? 5 : 10;
  }

  public get currency(): string {
    return (this.data.questionOptions || this.data.openQuestionOptions).currency;
  }

  public get multiChoice(): string {
    return this.contents ? this.contents.filter(text => !!text).join(', ') : this.content;
  }

  public get hasContent(): boolean {
    return (
      !!this.content ||
      !!this.multiChoice ||
      this.attachments!.length > 0 ||
      this.kind === QuestionKind.Boolean ||
      this.kind === QuestionKind.Legal
    );
  }

  constructor(
    @Inject(CandidateQuestionsTranslations)
    public translations: CandidateQuestionsModuleTranslations,
  ) {}

  public attachmentsTrackBy(index: number, item: Attachment): number {
    return item!.id;
  }
}
